
import { useState, useCallback, useEffect } from 'react';
import { fetchAllImoveisData, fetchAllImoveisAluguelData, fetchSairamUltimaSemanaData, fetchEntraramUltimaSemanaData } from '@/integrations/supabase/client';
import { Property, PropertyFilters } from '@/types/property';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

interface CacheData {
  properties: Property[];
  uniqueValues: UniqueFilterValues;
  stats: DashboardStats;
  timestamp: number;
}

type DashboardType = 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const MINIMAL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes para cache mínimo

const isCacheValid = (cache: CacheData): boolean => {
  const cacheAge = Date.now() - cache.timestamp;
  return cacheAge < CACHE_DURATION;
};

const isMinimalCacheValid = (cache: CacheData): boolean => {
  const cacheAge = Date.now() - cache.timestamp;
  return cacheAge < MINIMAL_CACHE_DURATION;
};

const getCachedData = (dashboardType: DashboardType): CacheData | null => {
  const cacheKey = 
    dashboardType === 'venda' ? 'dashboardCache' : 
    dashboardType === 'aluguel' ? 'rentalDashboardCache' : 
    dashboardType === 'sairam-ultima-semana' ? 'sairamUltimaSemanaCache' :
    'entraramUltimaSemanaCache';
  
  // Primeiro tenta o sessionStorage para cache completo
  const cachedString = sessionStorage.getItem(cacheKey);
  if (cachedString) {
    try {
      const data = JSON.parse(cachedString) as CacheData;
      if (isCacheValid(data)) {
        return data;
      }
    } catch (e) {
      console.error(`Error parsing ${dashboardType} dashboard cache`);
    }
  }
  
  // Se não encontrou no sessionStorage, tenta o localStorage para cache mínimo
  const minimalCacheKey = `${cacheKey}_minimal`;
  const minimalCachedString = localStorage.getItem(minimalCacheKey);
  if (minimalCachedString) {
    try {
      const data = JSON.parse(minimalCachedString) as CacheData;
      if (isMinimalCacheValid(data)) {
        return data;
      }
    } catch (e) {
      console.error(`Error parsing ${dashboardType} minimal dashboard cache`);
    }
  }
  
  return null;
};

const setCachedData = (data: CacheData, dashboardType: DashboardType): void => {
  try {
    const timestampKey = 
      dashboardType === 'venda' ? 'dashboardDataTimestamp' : 
      dashboardType === 'aluguel' ? 'rentalDashboardDataTimestamp' : 
      dashboardType === 'sairam-ultima-semana' ? 'sairamUltimaSemanaDataTimestamp' : 
      'entraramUltimaSemanaDataTimestamp';
    const cacheKey = 
      dashboardType === 'venda' ? 'dashboardCache' : 
      dashboardType === 'aluguel' ? 'rentalDashboardCache' : 
      dashboardType === 'sairam-ultima-semana' ? 'sairamUltimaSemanaCache' : 
      'entraramUltimaSemanaCache';
    
    localStorage.setItem(timestampKey, data.timestamp.toString());
    
    try {
      // Tenta armazenar cache completo no sessionStorage
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (storageError) {
      console.warn(`Error storing ${dashboardType} full dashboard cache, trying minimal cache`);
      
      try {
        // Se falhar, armazena cache mínimo no localStorage
        const minimalCache: CacheData = {
          properties: [],
          uniqueValues: data.uniqueValues,
          stats: data.stats,
          timestamp: data.timestamp
        };
        const minimalCacheKey = `${cacheKey}_minimal`;
        localStorage.setItem(minimalCacheKey, JSON.stringify(minimalCache));
        console.log('Stored minimal cache without properties data');
      } catch (minimalCacheError) {
        console.error('Failed to store even minimal cache');
      }
    }
  } catch (e) {
    console.error('Error storing dashboard cache metadata');
  }
};

export interface DashboardStats {
  totalProperties: number;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  avgPricePerSqMeter: number;
  avgPricePerUsefulSqMeter: number;
  priceIncreasedCount: number;
  priceDecreasedCount: number;
  minBedrooms: number;
  maxBedrooms: number;
  minBathrooms: number;
  maxBathrooms: number;
  minParkingSpaces: number;
  maxParkingSpaces: number;
  minUsefulArea: number;
  maxUsefulArea: number;
  minTotalArea: number;
  maxTotalArea: number;
  minDiasAnunciado: number;
  maxDiasAnunciado: number;
}

export interface UniqueFilterValues {
  finalidade: string[];
  tipo: string[];
  bairro: string[];
  cidade: string[];
  origem: string[];
  variacao: string[];
}

export const useUnifiedDashboardData = (dashboardType: DashboardType = 'venda') => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [filters, setFilters] = useState<PropertyFilters>({});
  const [uniqueValues, setUniqueValues] = useState<UniqueFilterValues>({
    finalidade: [],
    tipo: [],
    bairro: [],
    cidade: [],
    origem: [],
    variacao: [],
  });
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    avgPrice: 0,
    minPrice: 0,
    maxPrice: 0,
    avgPricePerSqMeter: 0,
    avgPricePerUsefulSqMeter: 0,
    priceIncreasedCount: 0,
    priceDecreasedCount: 0,
    minBedrooms: 0,
    maxBedrooms: 0,
    minBathrooms: 0,
    maxBathrooms: 0,
    minParkingSpaces: 0,
    maxParkingSpaces: 0,
    minUsefulArea: 0,
    maxUsefulArea: 0,
    minTotalArea: 0,
    maxTotalArea: 0,
    minDiasAnunciado: 0,
    maxDiasAnunciado: 0
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [databaseError, setDatabaseError] = useState<string | null>(null);
  const [lastFetchAttempt, setLastFetchAttempt] = useState<number>(0);

  const FETCH_DEBOUNCE = 2000; // 2 segundos

  useEffect(() => {
    const cachedData = getCachedData(dashboardType);
    if (cachedData && isCacheValid(cachedData)) {
      console.log(`Using cached ${dashboardType} data, age: ${Math.floor((Date.now() - cachedData.timestamp) / 60000)}min`);
      
      if (cachedData.properties && cachedData.properties.length > 0) {
        setProperties(cachedData.properties);
        setFilteredProperties(cachedData.properties);
      }
      
      setUniqueValues(cachedData.uniqueValues);
      setStats(cachedData.stats);
      setDataLoaded(true);
      
      const age = Math.floor((Date.now() - cachedData.timestamp) / 60000);
      if (age < 60) {
        toast.info(`Usando dados em cache de ${age} minutos atrás`);
      }
    }
  }, [dashboardType]);

  const safeParseFloat = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d,\.]/g, '').replace(',', '.');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const extractUniqueValuesFromProperties = (props: Property[]): UniqueFilterValues => {
    const uniqueFinalidades = Array.from(new Set(props.map(item => item.finalidade).filter(Boolean)));
    const uniqueTipos = Array.from(new Set(props.map(item => item.tipo).filter(Boolean)));
    const uniqueBairros = Array.from(new Set(props.map(item => item.bairro).filter(Boolean)));
    const uniqueCidades = Array.from(new Set(props.map(item => item.cidade).filter(Boolean)));
    const uniqueOrigens = Array.from(new Set(props.map(item => item.origem).filter(Boolean)));
    
    const uniqueVariacoes = Array.from(new Set(props.map(item => 
      item.variacao || 'Igual'
    )));

    return {
      finalidade: uniqueFinalidades,
      tipo: uniqueTipos,
      bairro: uniqueBairros,
      cidade: uniqueCidades,
      origem: uniqueOrigens,
      variacao: uniqueVariacoes,
    };
  };

  const calculateStats = useCallback((props: Property[]) => {
    const prices = props
      .map((p) => safeParseFloat(p.valor_recente))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) > 0) as number[];

    const avgPrice = prices.length ? prices.reduce((sum, val) => sum + val, 0) / prices.length : 0;
    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;

    const pricesPerSqMeter = props
      .map((p) => {
        const price = safeParseFloat(p.valor_recente);
        if (!price || price <= 0) return null;

        const area = safeParseFloat(p.area_total);
        if (!area || area <= 0) return null;

        return price / area;
      })
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) > 0) as number[];

    const avgPricePerSqMeter = pricesPerSqMeter.length
      ? pricesPerSqMeter.reduce((sum, val) => sum + val, 0) / pricesPerSqMeter.length
      : 0;

    const pricesPerUsefulSqMeter = props
      .map((p) => {
        const price = safeParseFloat(p.valor_recente);
        if (!price || price <= 0) return null;

        const area = safeParseFloat(p.area_util);
        if (!area || area <= 0) return null;

        return price / area;
      })
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) > 0) as number[];

    const avgPricePerUsefulSqMeter = pricesPerUsefulSqMeter.length
      ? pricesPerUsefulSqMeter.reduce((sum, val) => sum + val, 0) / pricesPerUsefulSqMeter.length
      : 0;

    const priceIncreasedCount = props.filter(p => p.variacao === 'Subiu').length;
    const priceDecreasedCount = props.filter(p => p.variacao === 'Desceu').length;

    const bedrooms = props
      .map((p) => safeParseFloat(p.numero_quartos))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) >= 0) as number[];
    
    const bathrooms = props
      .map((p) => safeParseFloat(p.numero_banheiros))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) >= 0) as number[];
    
    const parkingSpaces = props
      .map((p) => safeParseFloat(p.numero_vagas))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) >= 0) as number[];
    
    const usefulAreas = props
      .map((p) => safeParseFloat(p.area_util))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) > 0) as number[];
    
    const totalAreas = props
      .map((p) => safeParseFloat(p.area_total))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) > 0) as number[];
    
    const diasAnunciado = props
      .map((p) => safeParseFloat(p.dias_desde_a_ultima_atualizacao))
      .filter((p) => p !== null && !isNaN(p as number) && (p as number) >= 0) as number[];

    const minBedrooms = bedrooms.length ? Math.min(...bedrooms) : 0;
    const maxBedrooms = bedrooms.length ? Math.max(...bedrooms) : 10;
    
    const minBathrooms = bathrooms.length ? Math.min(...bathrooms) : 0;
    const maxBathrooms = bathrooms.length ? Math.max(...bathrooms) : 10;
    
    const minParkingSpaces = parkingSpaces.length ? Math.min(...parkingSpaces) : 0;
    const maxParkingSpaces = parkingSpaces.length ? Math.max(...parkingSpaces) : 10;
    
    const minUsefulArea = usefulAreas.length ? Math.min(...usefulAreas) : 0;
    const maxUsefulArea = usefulAreas.length ? Math.max(...usefulAreas) : 2000;
    
    const minTotalArea = totalAreas.length ? Math.min(...totalAreas) : 0;
    const maxTotalArea = totalAreas.length ? Math.max(...totalAreas) : 20000;
    
    const minDiasAnunciado = diasAnunciado.length ? Math.min(...diasAnunciado) : 0;
    const maxDiasAnunciado = diasAnunciado.length ? Math.max(...diasAnunciado) : 365;

    return {
      totalProperties: props.length,
      avgPrice,
      minPrice,
      maxPrice,
      avgPricePerSqMeter,
      avgPricePerUsefulSqMeter,
      priceIncreasedCount,
      priceDecreasedCount,
      minBedrooms,
      maxBedrooms,
      minBathrooms,
      maxBathrooms,
      minParkingSpaces,
      maxParkingSpaces,
      minUsefulArea,
      maxUsefulArea,
      minTotalArea,
      maxTotalArea,
      minDiasAnunciado,
      maxDiasAnunciado
    };
  }, []);

  const applyFilters = useCallback((newFilters: PropertyFilters) => {
    console.log(`Applying filters to ${properties.length} properties`);
    setFilters(newFilters);

    let filtered = [...properties];

    if (newFilters.finalidade?.length) {
      filtered = filtered.filter(item => 
        item.finalidade && newFilters.finalidade.includes(item.finalidade)
      );
    }

    if (newFilters.tipo?.length) {
      filtered = filtered.filter(item => 
        item.tipo && newFilters.tipo.includes(item.tipo)
      );
    }

    if (newFilters.bairro?.length) {
      filtered = filtered.filter(item => 
        item.bairro && newFilters.bairro.includes(item.bairro)
      );
    }

    if (newFilters.cidade?.length) {
      filtered = filtered.filter(item => 
        item.cidade && newFilters.cidade.includes(item.cidade)
      );
    }

    if (newFilters.origem?.length) {
      filtered = filtered.filter(item => 
        item.origem && newFilters.origem.includes(item.origem)
      );
    }

    if (newFilters.variacao?.length) {
      filtered = filtered.filter(item => {
        const itemVariacao = item.variacao || 'Igual';
        return newFilters.variacao?.includes(itemVariacao);
      });
    }

    if (newFilters.tempoAnunciadoMin !== undefined && newFilters.tempoAnunciadoMin >= 0) {
      filtered = filtered.filter(item => {
        const dias = safeParseFloat(item.dias_desde_a_ultima_atualizacao);
        return dias !== null && dias >= newFilters.tempoAnunciadoMin!;
      });
    }

    if (newFilters.tempoAnunciadoMax !== undefined && newFilters.tempoAnunciadoMax < 365) {
      filtered = filtered.filter(item => {
        const dias = safeParseFloat(item.dias_desde_a_ultima_atualizacao);
        return dias !== null && dias <= newFilters.tempoAnunciadoMax!;
      });
    }

    if (newFilters.valorMin !== undefined && newFilters.valorMin > 0) {
      filtered = filtered.filter(item => {
        const price = safeParseFloat(item.valor_recente);
        return price !== null && price >= newFilters.valorMin!;
      });
    }

    if (newFilters.valorMax !== undefined && newFilters.valorMax < Number.MAX_SAFE_INTEGER) {
      filtered = filtered.filter(item => {
        const price = safeParseFloat(item.valor_recente);
        return price !== null && price <= newFilters.valorMax!;
      });
    }

    if (newFilters.quartoMin !== undefined && newFilters.quartoMin > 0) {
      filtered = filtered.filter(item => {
        const quartos = safeParseFloat(item.numero_quartos);
        return quartos !== null && quartos >= newFilters.quartoMin!;
      });
    }

    if (newFilters.quartoMax !== undefined && newFilters.quartoMax < 10) {
      filtered = filtered.filter(item => {
        const quartos = safeParseFloat(item.numero_quartos);
        return quartos !== null && quartos <= newFilters.quartoMax!;
      });
    }

    if (newFilters.banheiroMin !== undefined && newFilters.banheiroMin > 0) {
      filtered = filtered.filter(item => {
        const banheiros = safeParseFloat(item.numero_banheiros);
        return banheiros !== null && banheiros >= newFilters.banheiroMin!;
      });
    }

    if (newFilters.banheiroMax !== undefined && newFilters.banheiroMax < 10) {
      filtered = filtered.filter(item => {
        const banheiros = safeParseFloat(item.numero_banheiros);
        return banheiros !== null && banheiros <= newFilters.banheiroMax!;
      });
    }

    if (newFilters.vagaMin !== undefined && newFilters.vagaMin > 0) {
      filtered = filtered.filter(item => {
        const vagas = safeParseFloat(item.numero_vagas);
        return vagas !== null && vagas >= newFilters.vagaMin!;
      });
    }

    if (newFilters.vagaMax !== undefined && newFilters.vagaMax < 10) {
      filtered = filtered.filter(item => {
        const vagas = safeParseFloat(item.numero_vagas);
        return vagas !== null && vagas <= newFilters.vagaMax!;
      });
    }

    if (newFilters.areaUtilMin !== undefined && newFilters.areaUtilMin > 0) {
      filtered = filtered.filter(item => {
        const area = safeParseFloat(item.area_util);
        return area !== null && area >= newFilters.areaUtilMin!;
      });
    }

    if (newFilters.areaUtilMax !== undefined && newFilters.areaUtilMax < 2000) {
      filtered = filtered.filter(item => {
        const area = safeParseFloat(item.area_util);
        return area !== null && area <= newFilters.areaUtilMax!;
      });
    }

    if (newFilters.areaTotalMin !== undefined && newFilters.areaTotalMin > 0) {
      filtered = filtered.filter(item => {
        const area = safeParseFloat(item.area_total);
        return area !== null && area >= newFilters.areaTotalMin!;
      });
    }

    if (newFilters.areaTotalMax !== undefined && newFilters.areaTotalMax < 20000) {
      filtered = filtered.filter(item => {
        const area = safeParseFloat(item.area_total);
        return area !== null && area <= newFilters.areaTotalMax!;
      });
    }

    console.log(`Filtered to ${filtered.length} properties`);
    setFilteredProperties(filtered);
  }, [properties, safeParseFloat]);

  const fetchInitialData = useCallback(async () => {
    const now = Date.now();
    if (now - lastFetchAttempt < FETCH_DEBOUNCE) {
      console.log(`Skipping fetch, last attempt was ${now - lastFetchAttempt}ms ago`);
      return true;
    }
    setLastFetchAttempt(now);

    console.log(`Checking cache for ${dashboardType} dashboard...`);
    
    const timestampKey = 
      dashboardType === 'venda' ? 'dashboardDataTimestamp' : 
      dashboardType === 'aluguel' ? 'rentalDashboardDataTimestamp' : 
      dashboardType === 'sairam-ultima-semana' ? 'sairamUltimaSemanaDataTimestamp' : 
      'entraramUltimaSemanaDataTimestamp';
    const timestampStr = localStorage.getItem(timestampKey);
    
    if (timestampStr) {
      const timestamp = parseInt(timestampStr, 10);
      const cacheAge = Date.now() - timestamp;
      
      if (cacheAge < CACHE_DURATION) {
        const cachedData = getCachedData(dashboardType);
        if (cachedData) {
          console.log(`Using cached data from ${Math.round(cacheAge / 60000)}min ago`);
          
          if (cachedData.properties && cachedData.properties.length > 0) {
            setProperties(cachedData.properties);
            setFilteredProperties(cachedData.properties);
          } else {
            console.log('Using minimal cache, fetching properties...');
            await fetchPropertiesOnly();
          }
          
          setUniqueValues(cachedData.uniqueValues);
          setStats(cachedData.stats);
          setDataLoaded(true);
          setLoading(false);
          setDatabaseError(null);
          
          const age = Math.floor(cacheAge / 60000);
          if (age < 60) {
            toast.info(`Usando dados em cache de ${age} minutos atrás`);
          }
          
          return true;
        }
      }
    }
    
    console.log(`Fetching fresh ${dashboardType} data...`);
    setLoading(true);
    setDatabaseError(null);
    
    try {
      const fetchFunction = 
        dashboardType === 'venda' ? fetchAllImoveisData : 
        dashboardType === 'aluguel' ? fetchAllImoveisAluguelData : 
        dashboardType === 'sairam-ultima-semana' ? fetchSairamUltimaSemanaData : 
        fetchEntraramUltimaSemanaData;
      const { data, error, message } = await fetchFunction();
      
      if (error) {
        console.error(`Error fetching ${dashboardType} data:`, error.message || 'Erro desconhecido');
        setDatabaseError(error.message || message || 'Erro ao buscar dados');
        setLoading(false);
        return false;
      }
      
      if (!data || data.length === 0) {
        console.warn(`No ${dashboardType} data returned from database`);
        setDatabaseError('Não foram encontrados dados na base de dados');
        setLoading(false);
        return false;
      }
      
      console.log(`Fetched ${data.length} ${dashboardType} properties`);
      
      const uniqueVals = extractUniqueValuesFromProperties(data);
      const statsData = calculateStats(data);
      
      setProperties(data);
      setFilteredProperties(data);
      setUniqueValues(uniqueVals);
      setStats(statsData);
      setDataLoaded(true);
      
      const newCacheData = {
        properties: data,
        uniqueValues: uniqueVals,
        stats: statsData,
        timestamp: Date.now()
      };
      
      setCachedData(newCacheData, dashboardType);
      
      toast.success(`${data.length.toLocaleString('pt-BR')} imóveis carregados`);
    } catch (e: any) {
      console.error(`Error in fetchInitialData for ${dashboardType}:`, e?.message || 'Erro desconhecido');
      setDatabaseError(e?.message || 'Erro inesperado ao buscar dados');
      return false;
    } finally {
      setLoading(false);
    }
    
    return true;
  }, [calculateStats, dashboardType, lastFetchAttempt]);

  const fetchPropertiesOnly = async (): Promise<boolean> => {
    setLoading(true);
    try {
      const fetchFunction = 
        dashboardType === 'venda' ? fetchAllImoveisData : 
        dashboardType === 'aluguel' ? fetchAllImoveisAluguelData : 
        dashboardType === 'sairam-ultima-semana' ? fetchSairamUltimaSemanaData :
        fetchEntraramUltimaSemanaData;
      const { data, error, message } = await fetchFunction();
      
      if (error) {
        console.error(`Error fetching ${dashboardType} properties:`, error.message || 'Erro desconhecido');
        setDatabaseError(error.message || message || 'Erro ao buscar dados');
        return false;
      }
      
      if (!data || data.length === 0) {
        console.warn(`No ${dashboardType} properties returned from database`);
        setDatabaseError('Não foram encontrados dados na base de dados');
        return false;
      }
      
      console.log(`Fetched ${data.length} ${dashboardType} properties`);
      setProperties(data);
      setFilteredProperties(data);
      return true;
    } catch (e: any) {
      console.error(`Error in fetchPropertiesOnly for ${dashboardType}:`, e?.message || 'Erro desconhecido');
      setDatabaseError(e?.message || 'Erro inesperado ao buscar dados');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    properties,
    filteredProperties,
    uniqueValues,
    stats,
    filters,
    dataLoaded,
    databaseError,
    applyFilters,
    fetchInitialData,
    calculateStats
  };
};
